import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { Avatar, AvatarSize } from '../../../../components/Avatar/Avatar';
import { FollowButton } from '../../../../components/Clickables/Buttons/FollowButton';
import { InternalLink } from '../../../../components/Clickables/InternalLink/InternalLink';
import { Heading } from '../../../../components/Heading/Heading';
import { useUserInfo } from '@domain/user/useUserInfo';
import { abbreviateNumber } from '@lib/numbers/abbreviateNumber';
import { getCoverImage } from '@domain/pages/getCoverImage';
import { getPageUrl } from '@domain/routes/urls';
import { DiscoveredBrandNodeFields } from '../../../../generated/graphql';
import { getBrandIsFollowed } from '@graphql/queries/rutilus/Brands';

interface IProps {
  brand: DiscoveredBrandNodeFields;
}

export const BrandCard = ({ brand }: IProps) => {
  const { isLoggedIn } = useUserInfo();
  const { _id, logo, displayName, followers, coverImages } = brand;
  const coverImage = getCoverImage(coverImages);
  const { data: followData, loading } = useRutilusQuery(getBrandIsFollowed, {
    variables: { externalId: _id },
    skip: !isLoggedIn,
  });
  const followedByCurrentUser = followData?.entry.followedByCurrentUser ?? false;

  const showFollowButton = !isLoggedIn || (!loading && followData);
  return (
    <div
      style={{
        background: `linear-gradient(rgba(0,0,0,0),rgba(0,0,0,8)), center / cover no-repeat url("${coverImage}")`,
        minWidth: 200,
        minHeight: 180,
        marginRight: '8px',
      }}
    >
      <InternalLink to={getPageUrl(_id, displayName)} variant="textPrimaryDarkBg">
        <div className="flex h-36 flex-col items-center">
          <div className="self-start p-2">
            {logo?.url && <Avatar alt={displayName} src={logo.url} size={AvatarSize.SMALL} />}
          </div>
          <Heading
            variant="2xs"
            as="h3"
            color="light"
            className="mt-auto truncate text-center"
            title={displayName}
          >
            {displayName}
          </Heading>
          <div className="body-text-sm mb-2 text-white">
            Followed by {abbreviateNumber(followers.totalCount)} anglers
          </div>
        </div>
      </InternalLink>
      {showFollowButton && (
        <FollowButton
          source="brand-follow-button"
          id={brand._id}
          isFollowed={followedByCurrentUser}
          buttonProps={{ className: 'w-full', style: { borderRadius: 0 } }}
          customAppDownloadHeader={
            followedByCurrentUser
              ? `Download the Fishbrain app to unfollow ${displayName}`
              : `Download the Fishbrain app to follow ${displayName}`
          }
        />
      )}
    </div>
  );
};
